import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Tag, Loader2, AlertTriangle, Save, Package, TrendingDown, Flame,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmt(c) {
  return ((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export default function DestockageAdmin() {
  const [products, setProducts] = useState([]);
  const [config, setConfig] = useState({ banner_enabled: true, banner_title: "", banner_subtitle: "" });
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/jardins-ole/admin/destockage/products"),
        api.get("/jardins-ole/admin/destockage/config"),
      ]);
      setProducts(pRes.data || []);
      setConfig(cRes.data || {});
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const payload = {
        banner_enabled: !!config.banner_enabled,
        banner_title: config.banner_title || "",
        banner_subtitle: config.banner_subtitle || "",
      };
      await api.put("/jardins-ole/admin/destockage/config", payload);
      toast.success("Configuration enregistrée");
    } catch {
      toast.error("Erreur");
    } finally { setSavingConfig(false); }
  };

  const saveBadge = async (p) => {
    try {
      await api.put(`/jardins-ole/admin/destockage/products/${p.id}`, { badge_text: p.badge_text || "" });
      toast.success(`Badge mis à jour pour ${p.name.slice(0, 40)}`);
    } catch {
      toast.error("Erreur");
    }
  };

  const updateProductLocal = (id, badge_text) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, badge_text } : p));
  };

  const stats = useMemo(() => {
    const total = products.length;
    const lowStock = products.filter(p => p.stock_tracking && p.stock_qty <= 5).length;
    const avgDiscount = total ? Math.round(products.reduce((s, p) => s + p.discount_pct, 0) / total) : 0;
    return { total, lowStock, avgDiscount };
  }, [products]);

  return (
    <div className="p-6 space-y-5" data-testid="destockage-admin">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Flame className="w-6 h-6 text-red-600" /> Déstockage &amp; Promotions
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Tout produit avec un <strong>prix barré supérieur au prix actuel</strong> apparaît automatiquement ici.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Produits en promo</div>
          <div className="text-3xl font-bold mt-1 text-red-600">{stats.total}</div>
        </Card>
        <Card className={`p-4 ${stats.lowStock > 0 ? "border-amber-300 bg-amber-50" : ""}`}>
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Stock faible (≤5)</div>
          <div className="text-3xl font-bold mt-1 text-amber-700">{stats.lowStock}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Réduction moyenne</div>
          <div className="text-3xl font-bold mt-1 text-emerald-700">-{stats.avgDiscount}%</div>
        </Card>
      </div>

      {/* Config */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-base flex items-center gap-2">
              <Flame className="w-4 h-4 text-red-600" />Bannière "Déstockage en cours" sur la home
            </h3>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">Visible en mode Jardinerie quand au moins 1 produit est en promo</p>
          </div>
          <Switch
            checked={!!config.banner_enabled}
            onCheckedChange={(v) => setConfig({ ...config, banner_enabled: v })}
            data-testid="banner-toggle"
          />
        </div>
        {config.banner_enabled && (
          <div className="grid md:grid-cols-2 gap-3 pt-3 border-t border-[hsl(var(--border))]">
            <div>
              <Label>Titre de la bannière</Label>
              <Input value={config.banner_title || ""} onChange={(e) => setConfig({ ...config, banner_title: e.target.value })} placeholder="ex: Déstockage en cours" data-testid="banner-title-input" />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input value={config.banner_subtitle || ""} onChange={(e) => setConfig({ ...config, banner_subtitle: e.target.value })} placeholder="ex: Stocks limités" data-testid="banner-subtitle-input" />
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={saveConfig} disabled={savingConfig} data-testid="save-config-btn">
            {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer la configuration
          </Button>
        </div>
      </Card>

      {/* Products list */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      ) : products.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
          <div className="text-[hsl(var(--muted-foreground))]">
            Aucun produit en déstockage actuellement.<br />
            Pour activer un déstockage, ouvrez la fiche produit dans Admin → Produits et renseignez un <strong>prix barré supérieur au prix actuel</strong>.
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/40 text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="px-4 py-3 text-left w-16">Image</th>
                  <th className="px-4 py-3 text-left">Produit</th>
                  <th className="px-4 py-3 text-right">Prix</th>
                  <th className="px-4 py-3 text-right">Réduction</th>
                  <th className="px-4 py-3 text-right">Stock</th>
                  <th className="px-4 py-3 text-left">Badge personnalisé (optionnel)</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const imageUrl = p.featured_image
                    ? (p.featured_image.startsWith("http") ? p.featured_image : `${BACKEND}${p.featured_image}`)
                    : "";
                  const lowStock = p.stock_tracking && p.stock_qty <= 5;
                  return (
                    <tr key={p.id} className="border-t" data-testid={`destockage-row-${p.id}`}>
                      <td className="px-4 py-3">
                        {imageUrl ? (
                          <img src={imageUrl} alt="" className="w-12 h-12 rounded-md object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded-md bg-[hsl(var(--muted))]/30 flex items-center justify-center">
                            <Package className="w-5 h-5 text-[hsl(var(--muted-foreground))]/40" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <a href={`/admin/products/${p.id}`} className="font-medium hover:underline text-[color:var(--brand-navy)]" target="_blank" rel="noreferrer">
                          {p.name}
                        </a>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">/shop/{p.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-red-600">{fmt(p.price_cents)}</div>
                        <div className="text-xs line-through text-[hsl(var(--muted-foreground))]">{fmt(p.compare_at_price_cents)}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                          <TrendingDown className="w-3 h-3" />-{p.discount_pct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.stock_tracking ? (
                          <span className={`text-sm font-semibold ${lowStock ? "text-amber-700" : ""}`}>
                            {p.stock_qty}
                            {lowStock && <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-600" />}
                          </span>
                        ) : (
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">illimité</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Input
                            value={p.badge_text || ""}
                            onChange={(e) => updateProductLocal(p.id, e.target.value)}
                            placeholder='ex: "Dernières pièces"'
                            maxLength={60}
                            className="text-xs"
                            data-testid={`badge-input-${p.id}`}
                          />
                          <Button size="sm" variant="outline" onClick={() => saveBadge(p)} data-testid={`badge-save-${p.id}`}>
                            <Save className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
